import { useMemo } from 'react';
import { resolveMediaUrl } from '../../utils/helpers';

// Kept as a named re-export so the many existing profile call sites don't all
// have to change; the resolution itself lives in one place now, since three
// copies of it had already drifted — this one only handled a leading
// `/images/`, which is why bare storage keys rendered nowhere.
export const resolveImageUrl = resolveMediaUrl;

/**
 * Returns the profile image URL directly from the stored value.
 * profile_image in DB now stores the CloudFront path (/images/profile_images/...).
 * No API call needed.
 */
export const useProfileImage = (userId, profileImage) => {
  const imageUrl = useMemo(() => {
    if (!profileImage) return null;
    if (profileImage.startsWith('/images/')) return resolveImageUrl(profileImage); // new format — uploaded
    if (profileImage.startsWith('http')) return null;                               // invalid — not an uploaded image
    if (userId) return resolveImageUrl(`/images/profile_images/${userId}/${profileImage}`); // legacy bare filename
    return null;
  }, [userId, profileImage]);

  return { imageUrl, loading: false, error: null };
};

/**
 * Batch resolve profile image URLs for multiple users.
 * Synchronous — no API calls needed.
 */
export const batchFetchProfileImages = async (users) => {
  const results = new Map();
  for (const user of users) {
    if (!user.profileImage) continue;
    if (user.profileImage.startsWith('/images/')) {
      results.set(user.userId, resolveImageUrl(user.profileImage));
    } else if (!user.profileImage.startsWith('http') && user.userId) {
      results.set(user.userId, resolveImageUrl(`/images/profile_images/${user.userId}/${user.profileImage}`));
    }
    // absolute URLs are invalid — skip (no entry = no image = fallback avatar)
  }
  return results;
};

export const clearProfileImageCache = () => {};

export default useProfileImage;
